import React, { useMemo, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useInsert } from '../../hooks/useInsert';
import { WpDialog } from './DialogShell';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { BlockPreview, UserBlockPreview, CalendarOptions, useCalendarPrefs, useSavedBlocks } from '../ribbon/BuildingBlocksGallery';
import { BLOCK_CATEGORIES, USER_CATEGORY, blocksFor, blockContext, buildBlockObject } from '../../lib/buildingBlocks';
import { deleteBuildingBlock, apiErrorMessage } from '../../lib/projectApi';
import { LayoutTemplate, CalendarDays, Frame, Megaphone, BookmarkPlus, Search, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ICONS = { 'Page Parts': LayoutTemplate, Calendars: CalendarDays, 'Borders & Accents': Frame, Advertisements: Megaphone, [USER_CATEGORY]: BookmarkPlus };
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, '-');

/**
 * Building Block Library ("More Page Parts…"): every starter block and every saved block with large previews,
 * a search box, calendar options and single-click insert. props: { category }
 */
export const BuildingBlockLibraryDialog = ({ props }) => {
  const { state, closeDialog, openDialog } = useEditor();
  const ins = useInsert();
  const [category, setCategory] = useState(BLOCK_CATEGORIES.includes(props.category) || props.category === USER_CATEGORY ? props.category : 'Page Parts');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null); // { kind: 'starter'|'saved', id }
  const [cal, setCal] = useCalendarPrefs();
  const [reload, setReload] = useState(0);
  const isUser = category === USER_CATEGORY;
  const { schemeId, pages, businessInfo } = state;
  const ctx = useMemo(() => blockContext({ schemeId, pages, businessInfo }, cal), [schemeId, pages, businessInfo, cal]);
  const starters = useMemo(() => (isUser ? [] : blocksFor(category).map((b) => ({ block: b, object: buildBlockObject(b, ctx) }))), [category, ctx, isUser]);
  const { blocks: saved, setBlocks } = useSavedBlocks(category, reload);
  const q = query.trim().toLowerCase();
  const visibleStarters = starters.filter((s) => !q || s.block.name.toLowerCase().includes(q) || (s.block.group || '').toLowerCase().includes(q));
  const visibleSaved = (saved || []).filter((b) => !q || b.name.toLowerCase().includes(q) || (b.category || '').toLowerCase().includes(q));

  const insert = (item) => {
    if (!item) return;
    if (item.kind === 'starter') ins.insertBuildingBlock(item.block); else ins.insertUserBlock(item.block);
    closeDialog();
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete “${b.name}” from your saved blocks?`)) return;
    try { await deleteBuildingBlock(b.id); setBlocks((l) => (l || []).filter((x) => x.id !== b.id)); if (selected?.id === b.id) setSelected(null); toast.success('Building block deleted'); }
    catch (e) { toast.error(await apiErrorMessage(e, 'Could not delete the block.')); }
  };

  const tile = (key, kind, block, object, name, meta, extra) => {
    const isSel = selected?.id === key;
    return (
      <div key={key} className="relative">
        <button
          type="button"
          onClick={() => setSelected({ kind, id: key, block })}
          onDoubleClick={() => insert({ kind, block })}
          className={`w-full rounded-lg border p-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isSel ? 'border-primary bg-primary/10 ring-1 ring-primary' : 'border-border bg-background hover:border-primary/60 hover:bg-primary/5'}`}
          data-state={isSel ? 'selected' : undefined}
          data-testid={`library-block-${key}`}
        >
          <div className="h-[132px] w-full flex items-center justify-center rounded-md bg-[repeating-conic-gradient(hsl(var(--muted))_0%_25%,transparent_0%_50%)] bg-[length:14px_14px] overflow-hidden">
            {object ? <BlockPreview object={object} width={196} height={124} /> : <UserBlockPreview block={block} width={196} height={124} />}
          </div>
          <div className="mt-2 text-sm font-medium truncate">{name}</div>
          <div className="text-xs text-muted-foreground truncate">{meta}</div>
        </button>
        {extra}
      </div>
    );
  };

  return (
    <WpDialog
      name="building-block-library"
      title="Building Block Library"
      width="w-[960px]"
      onOk={() => insert(selected)}
      okLabel="Insert"
      okDisabled={!selected}
      onCancel={closeDialog}
      cancelLabel="Close"
    >
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 min-h-[460px]" data-testid="building-block-library">
        <aside className="md:border-r md:border-border md:pr-3">
          <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible" role="tablist" aria-label="Galleries">
            {[...BLOCK_CATEGORIES, USER_CATEGORY].map((c) => {
              const Icon = ICONS[c];
              const active = c === category;
              return (
                <button key={c} type="button" role="tab" aria-selected={active} onClick={() => { setCategory(c); setSelected(null); }} className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm text-left whitespace-nowrap transition-colors duration-150 ${active ? 'bg-primary text-primary-foreground' : 'hover:bg-accent text-foreground'}`} data-testid={`library-category-${slug(c)}`}>
                  <Icon className="h-4 w-4 shrink-0" /> {c}
                </button>
              );
            })}
          </div>
          <div className="hidden md:block mt-4 text-xs text-muted-foreground leading-relaxed">
            Blocks pick up your publication’s colour scheme and fonts. Insert, then double-click (or Ctrl+Shift+G) to edit the parts.
          </div>
          <Button variant="outline" size="sm" className="hidden md:inline-flex mt-3 w-full" disabled={!state.selectedObjectIds.length} onClick={() => { closeDialog(); openDialog('building-block', { objectIds: state.selectedObjectIds, category: isUser ? 'Page Parts' : category }); }} data-testid="library-add-selection">
            <BookmarkPlus className="h-4 w-4 mr-2" /> Add selection…
          </Button>
        </aside>

        <section className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${category}…`} className="h-8 pl-8" data-testid="library-search" />
            </div>
            {category === 'Calendars' && <CalendarOptions value={cal} onChange={setCal} compact />}
          </div>

          <div className="max-h-[480px] overflow-y-auto pr-1">
            {!isUser && (
              <>
                {visibleStarters.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="library-starters">
                    {visibleStarters.map(({ block, object }) => tile(block.id, 'starter', block, object, block.name, block.group))}
                  </div>
                )}
                {visibleStarters.length === 0 && q && <div className="text-sm text-muted-foreground py-6 text-center" data-testid="library-no-results">No starter blocks match “{query}”.</div>}
              </>
            )}

            <div className={`${!isUser ? 'mt-5' : ''}`}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><BookmarkPlus className="h-3.5 w-3.5" /> {isUser ? 'All saved blocks' : `My ${category}`}</div>
              {saved === null && <div className="text-sm text-muted-foreground flex items-center gap-2 py-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading saved blocks…</div>}
              {saved && visibleSaved.length === 0 && (
                <div className="rounded-md border border-dashed border-border p-5 text-center text-sm text-muted-foreground" data-testid="library-saved-empty">
                  {q ? 'No saved blocks match your search.' : 'Nothing saved yet. Select objects on the page, right-click and choose “Save as Building Block…”, or use “Add selection…”.'}
                </div>
              )}
              {saved && visibleSaved.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="library-saved">
                  {visibleSaved.map((b) => tile(b.id, 'saved', b, null, b.name, isUser ? b.category : 'Saved block', (
                    <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7 bg-background/80 backdrop-blur text-muted-foreground hover:text-destructive" onClick={() => remove(b)} aria-label={`Delete ${b.name}`} data-testid={`library-delete-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  )))}
                </div>
              )}
            </div>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">{selected ? `Selected: ${selected.block.name} — click Insert or double-click a block.` : 'Click a block to select it, double-click to insert.'}</div>
        </section>
      </div>
    </WpDialog>
  );
};
